import React, { useState, useEffect } from 'react';
import { Rocket, Sparkles, Crown, Check, TrendingUp, Eye, MousePointer, Calendar, X, Loader2 } from 'lucide-react';
import api from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Boost Listing Modal
const BoostListingModal = ({ listing, isOpen, onClose, onSuccess }) => {
  const [tiers, setTiers] = useState({});
  const [selectedTier, setSelectedTier] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTiers = async () => {
      try {
        const response = await api.get('/sponsored/tiers');
        setTiers(response.data.tiers);
      } catch (err) {
        console.error('Failed to fetch tiers:', err);
      }
    };
    if (isOpen) {
      fetchTiers();
    }
  }, [isOpen]);

  const handleBoost = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.post('/sponsored/create-session', {
        listing_id: listing.listing_id,
        tier: selectedTier,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start checkout');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const tierIcons = {
    basic: Rocket,
    plus: Sparkles,
    premium: Crown
  };

  const tierColors = {
    basic: 'from-blue-500 to-cyan-500',
    plus: 'from-purple-500 to-pink-500',
    premium: 'from-amber-500 to-orange-500'
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Rocket className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg">Boost Your Listing</h2>
              <p className="text-emerald-100 text-sm">Get more visibility and sell faster</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Listing Preview */}
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-4">
            <img 
              src={listing.images?.[0] || 'https://placehold.co/80x80/e2e8f0/64748b?text=Item'} 
              alt={listing.title}
              className="w-16 h-16 rounded-lg object-cover"
            />
            <div>
              <h3 className="font-semibold text-gray-900 line-clamp-1">{listing.title}</h3>
              <p className="text-emerald-600 font-bold">${listing.price?.toFixed(2)}</p>
            </div>
          </div>
        </div>

        {/* Tier Selection */}
        <div className="p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Choose Your Boost Package</h3>
          
          <div className="grid gap-4">
            {Object.entries(tiers).map(([key, tier]) => {
              const Icon = tierIcons[key] || Rocket;
              const isSelected = selectedTier === key;
              
              return (
                <button
                  key={key}
                  onClick={() => setSelectedTier(key)}
                  className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                    isSelected 
                      ? 'border-emerald-500 bg-emerald-50' 
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  {key === 'premium' && (
                    <div className="absolute -top-2 -right-2 bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full font-semibold">
                      Best Value
                    </div>
                  )}
                  
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${tierColors[key]} flex items-center justify-center`}>
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900">{tier.name}</h4>
                        <p className="text-sm text-gray-500">{tier.duration_days} days</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900">${tier.price}</p>
                      <p className="text-xs text-gray-500">${(tier.price / tier.duration_days).toFixed(2)}/day</p>
                    </div>
                  </div>
                  
                  <div className="mt-3 flex flex-wrap gap-2">
                    {tier.features?.map((feature, idx) => (
                      <span 
                        key={idx}
                        className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full"
                      >
                        <Check className="h-3 w-3 text-emerald-500" />
                        {feature}
                      </span>
                    ))}
                  </div>
                  
                  {isSelected && (
                    <div className="absolute top-4 right-4">
                      <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* CTA Button */}
          <button
            onClick={handleBoost}
            disabled={loading}
            className="w-full mt-6 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold rounded-xl transition flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Rocket className="h-5 w-5" />
                Boost for ${tiers[selectedTier]?.price || '4.99'}
              </>
            )}
          </button>
          
          <p className="text-center text-xs text-gray-500 mt-3">
            Secure payment powered by Stripe. Cancel anytime.
          </p>
        </div>
      </div>
    </div>
  );
};

// Sponsored Badge Component
const SponsoredBadge = ({ badge = "Sponsored", size = "sm" }) => {
  const badgeColors = {
    "Sponsored": "bg-blue-500",
    "Featured": "bg-purple-500",
    "Premium": "bg-gradient-to-r from-amber-500 to-orange-500"
  };

  const sizeClasses = {
    sm: "text-xs px-2 py-0.5",
    md: "text-sm px-3 py-1",
    lg: "text-base px-4 py-1.5"
  };

  return (
    <span className={`${badgeColors[badge] || badgeColors["Sponsored"]} ${sizeClasses[size]} text-white font-semibold rounded-full inline-flex items-center gap-1`}>
      <Sparkles className="h-3 w-3" />
      {badge}
    </span>
  );
};

// Sponsored Stats Card (for seller dashboard)
const SponsoredStatsCard = ({ listingId }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get(`/sponsored/stats/${listingId}`);
        setStats(response.data.stats);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [listingId]);

  if (loading) {
    return (
      <div className="bg-gray-50 rounded-xl p-4 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="h-8 bg-gray-200 rounded w-3/4"></div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-100">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-5 w-5 text-emerald-500" />
        <h4 className="font-semibold text-gray-900">Boost Performance</h4>
        <span className="ml-auto text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
          {stats.tier}
        </span>
      </div>
      
      <div className="grid grid-cols-3 gap-4">
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-gray-500 text-xs mb-1">
            <Eye className="h-3 w-3" /> Views
          </div>
          <p className="text-xl font-bold text-gray-900">{stats.impressions.toLocaleString()}</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-gray-500 text-xs mb-1">
            <MousePointer className="h-3 w-3" /> Clicks
          </div>
          <p className="text-xl font-bold text-gray-900">{stats.clicks.toLocaleString()}</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-gray-500 text-xs mb-1">
            <TrendingUp className="h-3 w-3" /> CTR
          </div>
          <p className="text-xl font-bold text-emerald-600">{stats.ctr}%</p>
        </div>
      </div>
      
      <div className="mt-3 pt-3 border-t border-emerald-100 flex items-center justify-between text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          Expires: {new Date(stats.end_date).toLocaleDateString()}
        </span>
        <span className="text-emerald-600 font-semibold">${stats.price_paid} paid</span>
      </div>
    </div>
  );
};

// Boost Button Component
const BoostButton = ({ listing, onBoostClick, size = "md" }) => {
  if (listing.is_sponsored) {
    return (
      <button
        disabled
        className="flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg font-medium cursor-not-allowed"
      >
        <Sparkles className="h-4 w-4" />
        Already Boosted
      </button>
    );
  }

  const sizeClasses = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2",
    lg: "px-6 py-3 text-lg"
  };

  return (
    <button
      onClick={onBoostClick}
      className={`flex items-center gap-2 ${sizeClasses[size]} bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-lg font-medium transition`}
    >
      <Rocket className="h-4 w-4" />
      Boost Listing
    </button>
  );
};

export { BoostListingModal, SponsoredBadge, SponsoredStatsCard, BoostButton };
export default BoostListingModal;
